import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MapPin, Globe, Maximize2, X, Plus, Minus, RotateCcw } from 'lucide-react';
import type { GeoIPResult } from '../services/geoip';

const MAP_W = 800;
const MAP_H = 400;
const WORLD_IMG =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Equirectangular-projection.jpg/1280px-Equirectangular-projection.jpg';

const MIN_SCALE = 1;
const MAX_SCALE = 12;

function toXY(lat: number, lng: number): [number, number] {
  const x = ((lng + 180) / 360) * MAP_W;
  const y = ((90 - lat) / 180) * MAP_H;
  return [Math.max(4, Math.min(MAP_W - 4, x)), Math.max(4, Math.min(MAP_H - 4, y))];
}

export function countryFlag(code: string | undefined) {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 127397 + c.charCodeAt(0)));
}

export interface SignupGeoPoint {
  ip: string;
  email?: string;
}

interface Cluster {
  key: string;
  geo: GeoIPResult;
  x: number;
  y: number;
  count: number;
  ips: string[];
}

interface Props {
  points: SignupGeoPoint[];
  geoData: Map<string, GeoIPResult>;
  loading?: boolean;
  locatedCount: number;
  totalCount: number;
  selectedCountry?: string;
  onSelectCountry?: (country: string) => void;
}

interface MapCanvasProps {
  clusters: Cluster[];
  maxCount: number;
  selectedCountry?: string;
  onSelectCountry?: (country: string) => void;
  hoveredKey: string | null;
  setHoveredKey: (key: string | null) => void;
  scale: number;
  interactive?: boolean;
}

/** The SVG map itself, rendered with a zoom/pan transform group. */
function MapCanvas({
  clusters,
  maxCount,
  selectedCountry,
  onSelectCountry,
  hoveredKey,
  setHoveredKey,
  scale,
  interactive,
}: MapCanvasProps) {
  return (
    <>
      <image
        href={WORLD_IMG}
        x="0"
        y="0"
        width={MAP_W}
        height={MAP_H}
        opacity="0.22"
        preserveAspectRatio="xMidYMid meet"
      />
      {clusters.map((c) => {
        const baseR = 4 + (c.count / maxCount) * 8;
        const r = baseR / Math.sqrt(scale); // keep dots readable when zoomed in
        const isHovered = hoveredKey === c.key;
        const isSelected = selectedCountry === c.geo.country;
        const color = isSelected ? '#fb923c' : '#f97316';
        return (
          <g
            key={c.key}
            onMouseEnter={() => setHoveredKey(c.key)}
            onMouseLeave={() => setHoveredKey(null)}
            onClick={(e) => {
              e.stopPropagation();
              onSelectCountry?.(selectedCountry === c.geo.country ? '' : c.geo.country);
            }}
            style={{ cursor: 'pointer' }}
          >
            <circle cx={c.x} cy={c.y} r={r + 7 / scale} fill={color} fillOpacity={0.12} />
            <circle cx={c.x} cy={c.y} r={r + 2 / scale} fill={color} fillOpacity={isHovered ? 0.45 : 0.22} />
            <circle cx={c.x} cy={c.y} r={r} fill={color} fillOpacity={0.9} />
            {c.count > 1 && (
              <text
                x={c.x}
                y={c.y + (3 / scale)}
                textAnchor="middle"
                fontSize={Math.max(6, Math.min(10, baseR)) / scale}
                fill="#0f172a"
                fontWeight="700"
                style={{ pointerEvents: 'none' }}
              >
                {c.count}
              </text>
            )}
            {isHovered && interactive && (
              <circle
                cx={c.x}
                cy={c.y}
                r={r + 9 / scale}
                fill="none"
                stroke={color}
                strokeWidth={1.5 / scale}
                strokeOpacity={0.7}
              />
            )}
          </g>
        );
      })}
    </>
  );
}

export default function SignupWorldMap({
  points,
  geoData,
  loading,
  locatedCount,
  totalCount,
  selectedCountry,
  onSelectCountry,
}: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const clusters = useMemo(() => {
    const byPlace = new Map<string, Cluster>();
    points.forEach((p) => {
      const geo = geoData.get(p.ip);
      if (!geo) return;
      const key = `${geo.country}|${geo.city}|${geo.lat.toFixed(1)}|${geo.lng.toFixed(1)}`;
      const existing = byPlace.get(key);
      if (existing) {
        existing.count += 1;
        existing.ips.push(p.ip);
      } else {
        const [x, y] = toXY(geo.lat, geo.lng);
        byPlace.set(key, { key, geo, x, y, count: 1, ips: [p.ip] });
      }
    });
    return Array.from(byPlace.values()).sort((a, b) => a.count - b.count);
  }, [points, geoData]);

  const countries = useMemo(() => {
    const counts = new Map<string, { count: number; code: string }>();
    points.forEach((p) => {
      const geo = geoData.get(p.ip);
      if (!geo) return;
      const cur = counts.get(geo.country) || { count: 0, code: geo.countryCode };
      cur.count += 1;
      counts.set(geo.country, cur);
    });
    return Array.from(counts.entries())
      .map(([country, v]) => ({ country, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [points, geoData]);

  const hovered = hoveredKey ? clusters.find((c) => c.key === hoveredKey) : null;
  const maxCount = Math.max(1, ...clusters.map((c) => c.count));
  const hasMap = clusters.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-orange-400" />
            <h3 className="text-sm font-semibold text-white">Where signups came from</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-slate-500">
              {locatedCount}/{totalCount} located
              {loading && (
                <span className="inline-flex items-center gap-1 ml-2">
                  <Loader2 size={11} className="animate-spin" /> mapping…
                </span>
              )}
            </span>
            {hasMap && (
              <button
                onClick={() => setLightboxOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg px-2.5 py-1.5"
                title="Expand map"
              >
                <Maximize2 size={13} /> Expand
              </button>
            )}
          </div>
        </div>

        {clusters.length === 0 && !loading ? (
          <div className="p-10 text-center">
            <MapPin size={28} className="mx-auto text-slate-700 mb-2" />
            <p className="text-sm text-slate-500">
              No session IPs to map in this window. Location is taken from the user’s first login IP.
            </p>
          </div>
        ) : (
          <div className="relative group">
            <svg
              viewBox={`0 0 ${MAP_W} ${MAP_H}`}
              className="w-full cursor-zoom-in"
              style={{ backgroundColor: '#0f172a' }}
              onClick={() => setLightboxOpen(true)}
            >
              <MapCanvas
                clusters={clusters}
                maxCount={maxCount}
                selectedCountry={selectedCountry}
                onSelectCountry={onSelectCountry}
                hoveredKey={hoveredKey}
                setHoveredKey={setHoveredKey}
                scale={1}
              />
            </svg>

            <div className="absolute bottom-3 left-3 text-[11px] text-slate-400 bg-black/50 rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Click to expand & zoom
            </div>

            {hovered && (
              <div className="absolute top-3 right-3 bg-black/90 border border-slate-700 rounded-xl px-4 py-3 text-xs space-y-0.5 pointer-events-none z-10 min-w-[170px]">
                <div className="text-white font-medium">
                  {countryFlag(hovered.geo.countryCode)} {hovered.geo.city}
                </div>
                <div className="text-slate-400">{hovered.geo.country}</div>
                <div className="text-slate-500 pt-1 border-t border-slate-800 mt-1">
                  {hovered.count} signup{hovered.count === 1 ? '' : 's'}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-white mb-3">By country</h3>
        {countries.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            {loading ? 'Resolving locations…' : 'No countries yet.'}
          </p>
        ) : (
          <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
            {countries.map((c) => (
              <button
                key={c.country}
                onClick={() => onSelectCountry?.(selectedCountry === c.country ? '' : c.country)}
                className={`w-full flex items-center justify-between text-sm rounded-lg px-2 py-1.5 ${
                  selectedCountry === c.country ? 'bg-slate-800' : 'hover:bg-slate-800/50'
                }`}
              >
                <span className="text-slate-300 truncate">
                  <span className="mr-1.5">{countryFlag(c.code)}</span>
                  {c.country}
                </span>
                <span className="text-white font-medium ml-2">
                  {c.count}
                  <span className="text-slate-500 ml-1.5 text-xs">
                    {locatedCount ? Math.round((c.count / locatedCount) * 100) : 0}%
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && (
        <MapLightbox
          clusters={clusters}
          maxCount={maxCount}
          selectedCountry={selectedCountry}
          onSelectCountry={onSelectCountry}
          locatedCount={locatedCount}
          totalCount={totalCount}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}

interface LightboxProps {
  clusters: Cluster[];
  maxCount: number;
  selectedCountry?: string;
  onSelectCountry?: (country: string) => void;
  locatedCount: number;
  totalCount: number;
  onClose: () => void;
}

function MapLightbox({
  clusters,
  maxCount,
  selectedCountry,
  onSelectCountry,
  locatedCount,
  totalCount,
  onClose,
}: LightboxProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const { scale, tx, ty } = view;
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const hovered = hoveredKey ? clusters.find((c) => c.key === hoveredKey) : null;

  const clampPan = useCallback((s: number, x: number, y: number) => {
    // Keep the map within view: translation range depends on the extra size.
    const minX = MAP_W * (1 - s);
    const minY = MAP_H * (1 - s);
    return {
      x: Math.min(0, Math.max(minX, x)),
      y: Math.min(0, Math.max(minY, y)),
    };
  }, []);

  // Convert a client point to viewBox coordinates.
  const toViewBox = useCallback((clientX: number, clientY: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { vx: 0, vy: 0 };
    return {
      vx: ((clientX - rect.left) / rect.width) * MAP_W,
      vy: ((clientY - rect.top) / rect.height) * MAP_H,
    };
  }, []);

  const zoomAt = useCallback(
    (clientX: number, clientY: number, nextScale: number) => {
      const { vx, vy } = toViewBox(clientX, clientY);
      setView((prev) => {
        const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
        // Keep the point under the cursor fixed: local = (v - t) / prevScale.
        const localX = (vx - prev.tx) / prev.scale;
        const localY = (vy - prev.ty) / prev.scale;
        const clamped = clampPan(s, vx - s * localX, vy - s * localY);
        return { scale: s, tx: clamped.x, ty: clamped.y };
      });
    },
    [toViewBox, clampPan]
  );

  const zoomButton = useCallback(
    (factor: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, scale * factor);
    },
    [scale, zoomAt]
  );

  const reset = useCallback(() => setView({ scale: 1, tx: 0, ty: 0 }), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomButton(1.3);
      else if (e.key === '-' || e.key === '_') zoomButton(1 / 1.3);
      else if (e.key === '0') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, zoomButton, reset]);

  // Native wheel listener so we can preventDefault (React onWheel is passive).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAt(e.clientX, e.clientY, scale * factor);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [scale, zoomAt]);

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty, moved: false };
    setIsDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dx = ((e.clientX - d.x) / rect.width) * MAP_W;
    const dy = ((e.clientY - d.y) / rect.height) * MAP_H;
    if (Math.abs(e.clientX - d.x) > 3 || Math.abs(e.clientY - d.y) > 3) d.moved = true;
    const clamped = clampPan(scale, d.tx + dx, d.ty + dy);
    setView((prev) => ({ ...prev, tx: clamped.x, ty: clamped.y }));
  };

  const endDrag = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-6xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-orange-400" />
            <h3 className="text-base font-semibold text-white">Where signups came from</h3>
            <span className="text-xs text-slate-400 ml-2">{locatedCount}/{totalCount} located</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg"
            title="Close (Esc)"
          >
            <X size={20} />
          </button>
        </div>

        <div className="relative bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${MAP_W} ${MAP_H}`}
            className="w-full select-none"
            style={{
              backgroundColor: '#0f172a',
              touchAction: 'none',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerLeave={endDrag}
          >
            <g transform={`translate(${tx}, ${ty}) scale(${scale})`}>
              <MapCanvas
                clusters={clusters}
                maxCount={maxCount}
                selectedCountry={selectedCountry}
                onSelectCountry={onSelectCountry}
                hoveredKey={hoveredKey}
                setHoveredKey={setHoveredKey}
                scale={scale}
                interactive
              />
            </g>
          </svg>

          {/* Zoom controls */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            <button
              onClick={() => zoomButton(1.3)}
              className="p-2 bg-slate-900/90 border border-slate-700 rounded-lg text-slate-200 hover:text-white hover:bg-slate-800"
              title="Zoom in (+)"
            >
              <Plus size={16} />
            </button>
            <button
              onClick={() => zoomButton(1 / 1.3)}
              className="p-2 bg-slate-900/90 border border-slate-700 rounded-lg text-slate-200 hover:text-white hover:bg-slate-800"
              title="Zoom out (-)"
            >
              <Minus size={16} />
            </button>
            <button
              onClick={reset}
              className="p-2 bg-slate-900/90 border border-slate-700 rounded-lg text-slate-200 hover:text-white hover:bg-slate-800"
              title="Reset (0)"
            >
              <RotateCcw size={16} />
            </button>
          </div>

          <div className="absolute bottom-3 left-3 text-[11px] text-slate-400 bg-black/50 rounded-md px-2 py-1 pointer-events-none">
            Scroll to zoom · drag to pan · {Math.round(scale * 100)}%
          </div>

          {hovered && (
            <div className="absolute top-3 right-3 bg-black/90 border border-slate-700 rounded-xl px-4 py-3 text-xs space-y-0.5 pointer-events-none z-10 min-w-[180px]">
              <div className="text-white font-medium">
                {countryFlag(hovered.geo.countryCode)} {hovered.geo.city}
              </div>
              <div className="text-slate-400">{hovered.geo.country}</div>
              <div className="text-slate-500 pt-1 border-t border-slate-800 mt-1">
                {hovered.count} signup{hovered.count === 1 ? '' : 's'}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
